import { type ReactNode, useEffect, useRef } from 'react';
import { createStaticAdapter } from '../adapters/static';
import type { FeatureFlagLogger } from '../adapters/types';
import { createUnleashAdapter } from '../adapters/unleash';
import { createFlagStore } from '../store/flag-store';
import type { EvaluationContext, FlagPropagator, FlagSnapshot, FlagStore } from '../store/types';
import { FeatureFlagContext } from './FeatureFlagContext';

interface StaticAdapterProps {
  type: 'static';
  flags?: FlagSnapshot;
}

interface UnleashAdapterProps {
  type: 'unleash';
  scriptId: string;
  logger?: FeatureFlagLogger;
}

type AdapterProps = StaticAdapterProps | UnleashAdapterProps;

type FeatureFlagProviderProps = AdapterProps & {
  initialContext?: EvaluationContext;
  propagators?: FlagPropagator[];
  children: ReactNode;
};

function createAdapter(props: AdapterProps) {
  switch (props.type) {
    case 'static':
      return createStaticAdapter(props.flags);
    case 'unleash':
      return createUnleashAdapter(props.scriptId, props.logger);
    default:
      throw new Error(`Unknown adapter type: ${(props as AdapterProps).type}`);
  }
}

export function FeatureFlagProvider({ children, initialContext, propagators, ...adapterProps }: FeatureFlagProviderProps) {
  const storeRef = useRef<FlagStore>(undefined);
  if (!storeRef.current) {
    storeRef.current = createFlagStore(createAdapter(adapterProps), { initialContext, propagators });
  }
  const store = storeRef.current;

  useEffect(() => () => store.destroy(), [store]);

  return <FeatureFlagContext.Provider value={store}>{children}</FeatureFlagContext.Provider>;
}
